import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import equal from 'deep-equal';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { UPLOAD_FILE_STATUS_KEY } from '@cosmotech/ui';
import { validateFieldValue } from './InputUtils';
import { useFamilleEquipements } from './hooks/FamilleEquipementsHook';
import {
  resolveScaleGompertz,
  resolveScaleWeibull,
  resolveShapeGompertz,
  resolveShapeWeibull,
  generateSurvivalData,
  generateFailureData,
  computeInitialPoints,
  maxTickTime,
} from './typeLoi.utils';

export const updateCurveParameters = (points, modeFiabilite, setErrorMessage, t) => {
  if (!points) return;

  const t1 = parseFloat(points.t1);
  const t2 = parseFloat(points.t2);
  const R1 = parseFloat(points.R1);
  const R2 = parseFloat(points.R2);

  if (isNaN(t1) || isNaN(t2) || isNaN(R1) || isNaN(R2)) {
    setErrorMessage(
      t(
        'commoncomponents.equipment.wizard.messages.invalidNumericValues',
        'Please enter valid numeric values for t1, t2, R1, and R2.'
      )
    );
    return;
  }

  if (t1 <= 0 || t2 <= 0 || t2 <= t1) {
    setErrorMessage(
      t(
        'commoncomponents.equipment.wizard.messages.positiveT1T2Required',
        'Ensure that t1 and t2 are positive with t2 > t1.'
      )
    );
    return;
  }

  if (R1 <= 0 || R1 >= 1 || R2 <= 0 || R2 >= 1) {
    setErrorMessage(
      t(
        'commoncomponents.equipment.wizard.messages.r1R2RangeError',
        'R1 and R2 must be numbers between 0 and 1 (exclusive).'
      )
    );
    return;
  }

  setErrorMessage('');

  try {
    if (modeFiabilite === 'Gompertz') {
      const b = resolveScaleGompertz(t1, t2, R1, R2);
      const eta = resolveShapeGompertz(b, t1, R1);
      return { Echelle: b, Forme: eta };
    } else if (modeFiabilite === 'Weibull') {
      const eta = resolveShapeWeibull(t1, t2, R1, R2);
      const lambda = resolveScaleWeibull(t1, R1, eta);
      return { Echelle: lambda, Forme: eta };
    }
  } catch (error) {
    console.error('Erreur dans le calcul des paramètres :', error);
    setErrorMessage(
      t(
        'commoncomponents.equipment.wizard.messages.parameterCalculationError',
        'An error occurred while calculating parameters. Please check your inputs.'
      )
    );
  }
};

const CHART_MODES = {
  SURVIVAL: 'survival',
  FAILURE: 'failure',
};

const FamilleEquipements = ({ context, mode = 'normal' }) => {
  const { t } = useTranslation();
  const [chartMode, setChartMode] = useState(CHART_MODES.SURVIVAL);
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [points, setPoints] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const formAndScaleInputRef = useRef({});

  const { agingLaws, equipmentFamily, savedPoints, savePoints } = useFamilleEquipements();
  const initialPointsState = useRef(null);

  useEffect(() => {
    const savedFamilyValue = agingLaws?.FamilleEquipement?.find((fe) => fe.Identifiant === equipmentFamily);
    if (!savedFamilyValue) return;

    formAndScaleInputRef.current = {
      Forme: savedFamilyValue.Forme,
      Echelle: savedFamilyValue.Echelle,
    };
  }, [equipmentFamily, agingLaws]);

  const chartConfigs = useMemo(
    () => ({
      [CHART_MODES.SURVIVAL]: {
        title: t('commoncomponents.equipment.wizard.chart.survivalFunctionCurve', 'Survival function curve'),
        yAxisLabel: t('commoncomponents.equipment.wizard.fields.survivalProbability', 'Survival probability (R)'),
        yDomain: ['auto', 1],
        valueLabel: 'R',
        getData: generateSurvivalData,
      },
      [CHART_MODES.FAILURE]: {
        title: t('commoncomponents.equipment.wizard.chart.failureRateFunctionCurve', 'Failure rate function curve'),
        yAxisLabel: t('commoncomponents.equipment.wizard.fields.failureRate', 'Failure rate (h)'),
        yDomain: [0, 'auto'],
        valueLabel: 'h',
        getData: generateFailureData,
      },
    }),
    [t]
  );

  const setNewFamilleEquipementValue = useCallback(
    (value, changedValues) => {
      return value.map((fe) => {
        if (fe.Identifiant === equipmentFamily) {
          return { ...fe, ...changedValues };
        }
        return fe;
      });
    },
    [equipmentFamily]
  );

  const handleDialogClose = useCallback(() => {
    setOpenDialog(false);
    setPoints(null);
    setErrorMessage('');
  }, []);

  const updatePoint = useCallback((key, value) => {
    setPoints((prevPoints) => {
      const newPoints = {
        ...(prevPoints || {}),
        [key]: value,
      };
      return newPoints;
    });
  }, []);

  const getPointValue = useCallback(
    (key) => {
      return points && points[key] !== undefined ? points[key] : '';
    },
    [points]
  );

  const defaultValue = useMemo(
    () => ({
      id: null,
      name: `FamilleEquipement.csv`,
      rows: agingLaws?.FamilleEquipement ?? [],
    }),
    [agingLaws]
  );

  const generateChartData = useCallback(
    (currentFamily) => {
      if (!currentFamily?.ModeFiabilite || !currentFamily?.Echelle || !currentFamily?.Forme) {
        return [];
      }
      return chartConfigs[chartMode].getData(currentFamily.ModeFiabilite, currentFamily.Echelle, currentFamily.Forme);
    },
    [chartMode, chartConfigs]
  );

  const generateXAxisTicks = useCallback((currentFamily) => {
    if (!currentFamily?.Echelle) return [];
    const maxTime = maxTickTime(currentFamily.ModeFiabilite, currentFamily.Forme, currentFamily.Echelle);
    const stepSize = Math.ceil(maxTime / 30);
    const numSteps = Math.floor(maxTime / stepSize) + 1;
    return Array.from({ length: numSteps }, (_, i) => i * stepSize);
  }, []);

  const handleCurveParameterUpdate = useCallback(
    (value, onChange) => {
      const currentFamily = value.rows?.find((fe) => fe.Identifiant === equipmentFamily);
      const newFormeAndEchelle = updateCurveParameters(points, currentFamily?.ModeFiabilite, setErrorMessage, t);
      if (!newFormeAndEchelle) return;

      const newValue = setNewFamilleEquipementValue(value.rows, newFormeAndEchelle);
      savePoints(points);
      initialPointsState.current = points;
      onChange({ ...value, status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD, rows: newValue });
      setOpenDialog(false);
    },
    [points, equipmentFamily, savePoints, setNewFamilleEquipementValue, t]
  );

  const isApplyPointsButtonDisabled = useMemo(() => {
    return equal(initialPointsState.current, points);
    // eslint rule is disabled in order to run comparison every time when the dialog is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, openDialog]);

  const handleEditCurveParameters = useCallback(
    (value) => {
      const currentFamily = value.rows?.find((fe) => fe.Identifiant === equipmentFamily);
      const initialPoints =
        savedPoints || computeInitialPoints(currentFamily.ModeFiabilite, currentFamily.Echelle, currentFamily.Forme);
      initialPointsState.current = initialPoints;
      setPoints(initialPoints);
      setOpenDialog(true);
    },
    [equipmentFamily, savedPoints]
  );

  if (!equipmentFamily)
    return <span>{t('commoncomponents.equipment.wizard.messages.noData', 'No data to display.')}</span>;
  const customizedLegend = (value) => {
    return (
      <Grid item sx={{ display: 'flex', px: 3, justifyContent: 'space-between' }}>
        {/* Commutateur pour la courbe de survie/panne */}
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography>{t('commoncomponents.equipment.wizard.chart.survival', 'Survival')}</Typography>
          <Switch
            checked={chartMode === CHART_MODES.FAILURE}
            onChange={(e) => setChartMode(e.target.checked ? CHART_MODES.FAILURE : CHART_MODES.SURVIVAL)}
            color="primary"
            inputProps={{ 'aria-label': 'switch between survival and failure mode' }}
          />
          <Typography>{t('commoncomponents.equipment.wizard.chart.breakdown', 'Breakdown')}</Typography>
        </Stack>
        <Typography variant="subtitle" sx={{ color: '#8884d8' }}>
          {chartConfigs[chartMode].title}
        </Typography>
        <Button
          disabled={!context.editMode || chartMode === CHART_MODES.FAILURE}
          color="primary"
          onClick={() => handleEditCurveParameters(value)}
        >
          {t('commoncomponents.equipment.wizard.chart.editButton', 'Edit')}
        </Button>
      </Grid>
    );
  };
  return (
    <div>
      <Controller
        name="FamilleEquipement"
        defaultValue={defaultValue}
        render={({ field }) => {
          const { value, onChange } = field;
          const handleFieldChange = (fieldName) => (e) => {
            const newValue = e.target.value;
            const isValid = validateFieldValue(fieldName, newValue);
            if (isValid) {
              const updatedValue = setNewFamilleEquipementValue(value.rows, { [fieldName]: newValue });
              onChange({ ...value, status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD, rows: updatedValue });
            }
          };
          const handleBlur = (fieldName) => () => {
            const currentFamily = value.rows?.find((fe) => fe.Identifiant === equipmentFamily);
            const currentValue = currentFamily?.[fieldName];
            const parsed = parseFloat(currentValue);

            if (currentValue === '' || isNaN(parsed) || parsed <= 0) {
              const fallback = formAndScaleInputRef.current[fieldName] ?? '';
              const updatedValue = setNewFamilleEquipementValue(value.rows, {
                [fieldName]: fallback,
              });
              onChange({ ...value, rows: updatedValue });
              return;
            }

            formAndScaleInputRef.current[fieldName] = currentValue;
          };
          return (
            <Grid container spacing={2}>
              <Grid item container xs={4} lg={2} spacing={2} marginTop={2} sx={{ flexDirection: 'column' }}>
                {mode === 'normal' && (
                  <>
                    <Grid item>
                      <TextField
                        data-cy="mode"
                        size="small"
                        select
                        label={t('commoncomponents.equipment.wizard.fields.reliabilityMode', 'Reliability Mode')}
                        disabled={!context.editMode}
                        value={value.rows?.find((fe) => fe.Identifiant === equipmentFamily)?.ModeFiabilite}
                        onChange={handleFieldChange('ModeFiabilite', (value) => value)}
                        fullWidth
                      >
                        <MenuItem data-cy="weibull" value="Weibull">
                          Weibull
                        </MenuItem>
                        <MenuItem data-cy="gompertz" value="Gompertz">
                          Gompertz
                        </MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item>
                      <TextField
                        data-cy="form"
                        size="small"
                        label={t('commoncomponents.equipment.wizard.fields.shape', 'Shape')}
                        disabled={!context.editMode}
                        value={value.rows?.find((fe) => fe.Identifiant === equipmentFamily)?.Forme ?? ''}
                        onChange={handleFieldChange('Forme')}
                        onBlur={handleBlur('Forme')}
                        fullWidth
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        data-cy="scale"
                        size="small"
                        label={t('commoncomponents.equipment.wizard.fields.scale', 'Scale')}
                        disabled={!context.editMode}
                        value={value.rows?.find((fe) => fe.Identifiant === equipmentFamily)?.Echelle ?? ''}
                        onChange={handleFieldChange('Echelle')}
                        onBlur={handleBlur('Echelle')}
                        fullWidth
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        data-cy="age-delta"
                        size="small"
                        label={t(
                          'commoncomponents.equipment.wizard.fields.referenceAgeWithDelta',
                          'Reference age for criterion with delta'
                        )}
                        inputProps={{ min: 0 }}
                        disabled={!context.editMode}
                        value={value.rows?.find((fe) => fe.Identifiant === equipmentFamily)?.AgeReferentDelta ?? 0}
                        onChange={handleFieldChange('AgeReferentDelta')}
                        fullWidth
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        data-cy="age-percentage"
                        size="small"
                        label={t(
                          'commoncomponents.equipment.wizard.fields.referenceAgeWithPercentage',
                          'Reference age for criterion with percentage'
                        )}
                        inputProps={{ min: 0 }}
                        disabled={!context.editMode}
                        value={
                          value.rows?.find((fe) => fe.Identifiant === equipmentFamily)?.AgeReferentPourcentage ?? 1
                        }
                        onChange={handleFieldChange('AgeReferentPourcentage')}
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
                <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="sm">
                  <DialogTitle>
                    {t('commoncomponents.equipment.wizard.chart.editCurvePoints', 'Edit curve points')}
                  </DialogTitle>
                  <DialogContent>
                    <Grid container spacing={2} padding={1}>
                      <Grid item xs={6}>
                        <TextField
                          label="t1"
                          type="number"
                          inputProps={{ min: 0 }}
                          value={getPointValue('t1')}
                          onChange={(e) => updatePoint('t1', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="R1"
                          type="number"
                          inputProps={{ step: 'any', min: 0, max: 1 }}
                          value={getPointValue('R1')}
                          onChange={(e) => updatePoint('R1', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="t2"
                          type="number"
                          inputProps={{ min: 0 }}
                          value={getPointValue('t2')}
                          onChange={(e) => updatePoint('t2', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="R2"
                          type="number"
                          inputProps={{ step: 'any', min: 0, max: 1 }}
                          value={getPointValue('R2')}
                          onChange={(e) => updatePoint('R2', e.target.value)}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    {errorMessage && <div style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</div>}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary">
                      {t('genericcomponent.common.cancel', 'CANCEL')}
                    </Button>
                    <Button
                      onClick={() => handleCurveParameterUpdate(value, onChange)}
                      color="primary"
                      disabled={isApplyPointsButtonDisabled}
                    >
                      {t('genericcomponent.common.apply', 'APPLY')}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Grid>
              {mode === 'defaillance' && (
                <Grid item xs={12} sx={{ padding: 2 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        data-cy="taux-panne-externe-defaillance"
                        size="small"
                        label={t(
                          'commoncomponents.equipment.wizard.fields.externalFailureRate',
                          'External Failure Rate (in % per year)'
                        )}
                        inputProps={{ min: 0, step: 'any' }}
                        disabled={!context.editMode}
                        value={value.rows?.find((fe) => fe.Identifiant === equipmentFamily)?.TauxPanneExterne ?? ''}
                        onChange={handleFieldChange('TauxPanneExterne')}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        data-cy="duree-quasi-panne-defaillance"
                        size="small"
                        label={t(
                          'commoncomponents.equipment.wizard.fields.nearFailureDuration',
                          'Near Failure Duration (in years)'
                        )}
                        inputProps={{ min: 0, step: 'any' }}
                        disabled={!context.editMode}
                        value={value.rows?.find((fe) => fe.Identifiant === equipmentFamily)?.DureeQuasiPanne ?? ''}
                        onChange={handleFieldChange('DureeQuasiPanne')}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {mode === 'normal' && (
                <>
                  {/* Bouton pour éditer les paramètres de courbe */}
                  <Grid container item xs={10} sx={{ flexDirection: 'column', mt: 1 }}>
                    {/* Affichage de la courbe */}
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={(() => {
                          const currentFamily = value.rows?.find((fe) => fe.Identifiant === equipmentFamily);
                          return generateChartData(currentFamily);
                        })()}
                        margin={{ top: 0, right: 20, bottom: 25, left: 25 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="t"
                          label={{
                            value: t('commoncomponents.equipment.wizard.fields.time', 'Time (t)'),
                            position: 'insideBottom',
                            offset: -15,
                          }}
                          tickFormatter={(tick) => tick.toFixed(0)}
                          interval={0}
                          ticks={(() => {
                            const currentFamily = value.rows?.find((fe) => fe.Identifiant === equipmentFamily);
                            return generateXAxisTicks(currentFamily);
                          })()}
                        />
                        <YAxis
                          domain={chartConfigs[chartMode].yDomain}
                          mirror={true}
                          label={{
                            value: chartConfigs[chartMode].yAxisLabel,
                            angle: -90,
                            position: 'insideLeft',
                            offset: -10,
                            style: {
                              textAnchor: 'middle',
                            },
                          }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            color: theme.palette.text.primary,
                          }}
                          labelStyle={{ color: theme.palette.text.secondary }}
                          itemStyle={{ color: '#8884d8' }}
                          formatter={(value) => {
                            return [`${value.toFixed(10)}`, chartConfigs[chartMode].valueLabel];
                          }}
                          labelFormatter={(label) => `t : ${label.toFixed(2)}`}
                        />
                        <Legend align="center" verticalAlign="top" content={() => customizedLegend(value)} />
                        <Line
                          type="monotone"
                          dataKey="R"
                          name={chartConfigs[chartMode].title}
                          stroke="#8884d8"
                          strokeWidth={3}
                          dot={false}
                          activeDot={{ r: 8 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Grid>
                </>
              )}
            </Grid>
          );
        }}
      />
    </div>
  );
};

FamilleEquipements.propTypes = {
  context: PropTypes.object,
  mode: PropTypes.oneOf(['normal', 'defaillance']),
};

export default FamilleEquipements;
